import {
    Box, Button, Heading, Text, VStack, Divider, Icon, useDisclosure, useToast,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, FormErrorMessage, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
    HStack, IconButton, Switch, Badge
} from '@chakra-ui/react';
import { FiLogOut, FiSettings, FiUser, FiLock, FiHome, FiTag, FiPlus, FiTrash2, FiSmartphone, FiAlertTriangle, FiBell } from 'react-icons/fi';
import { IoCarSportOutline } from 'react-icons/io5';
import { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { getUnionFeatures } from '../utils/institutionFeatures';
import InstitutionFeatureSwitches from '../components/InstitutionFeatureSwitches';
import StaffNotificationMatrix from '../components/StaffNotificationMatrix';

const SettingsPage = () => {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const { enableAudio } = useWebSocket(); // Use Audio Context

    // Password Modal State
    const { isOpen: isPassOpen, onOpen: onPassOpen, onClose: onPassClose } = useDisclosure();

    // Delete Account Modal State
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const toast = useToast();

    // Password Change State
    const [passwords, setPasswords] = useState({
        old_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [loading, setLoading] = useState(false);

    // Institution Settings State
    const [instSettings, setInstSettings] = useState({
        attendance_delay_minutes: 5,
        name: '',
        daily_reset_time: '03:00',
        prediction_start_time: '17:00',
        prediction_end_time: '20:00',
        closing_report_time: '21:30', // [Phase 20]
        line_notify_token: ''
    });
    const [instLoading, setInstLoading] = useState(false);

    // [A2] 機構品牌 / 年級標籤（僅 super_admin 顯示與可改，見下方渲染區塊）
    const [instFeatures, setInstFeatures] = useState({
        display_name: '',
        logo_url: '',
    });
    const [gradeLabelRows, setGradeLabelRows] = useState([]); // [{grade:'1', label:'幼幼班'}, ...]
    const [instFeaturesLoading, setInstFeaturesLoading] = useState(false);

    // [2026-08-17] App 功能（兩層授權模型的第二層，見 components/InstitutionFeatureSwitches.jsx）：
    //   granted（超管授予）＝這間機構「可以用」哪些 → 這裡唯讀，只用來把未授權項目標示成停用
    //   enabled（機構管理員）＝在授權範圍內「要不要開」 → 這裡是草稿，按儲存才送出
    // 後端 InstitutionOut.features 回的是**實際生效值**（granted AND enabled），拿來當
    // enabled 初值是安全的：未授權者一律 false，而它本來就不能被打開。
    const [featureGranted, setFeatureGranted] = useState({});
    const [featureEnabled, setFeatureEnabled] = useState({});
    const [featureSaving, setFeatureSaving] = useState(false);
    // 載入完成前一律鎖住：兩個 state 的初值 {} 會讓元件把每一項都當成「已授權且已啟用」而
    // 全部顯示為開，此時若手快按下儲存，等於把機構原本關掉的功能整批打開。
    const [featureLoaded, setFeatureLoaded] = useState(false);

    // [2026-08-20] 教職員通知設定（BL-005 通知類權限矩陣）
    // notifOptions：有哪些通知可設定、每項的說明與預設值 —— **一律由後端提供**。
    //   寫死在前端的話，後端改了預設值而這裡的說明不會跟著改，使用者看到的與系統
    //   實際做的就不一致了。
    // notifMatrix：這間機構目前的完整矩陣（後端已解析好，不需要再併預設值），是草稿，
    //   按儲存才送出。整張表一次存檔而不是逐格自動儲存：權限畫面上的誤點若立即生效
    //   又沒有 undo，代價太高。
    // notifBaseline：載入當下的值，送出時一併帶回去做樂觀鎖。兩位管理員同時開著這一頁時，
    //   後存的人會把先存的人整批蓋掉而雙方都看到「儲存成功」；帶了它後端會回 409。
    const [notifOptions, setNotifOptions] = useState(null);
    const [notifMatrix, setNotifMatrix] = useState({});
    const [notifBaseline, setNotifBaseline] = useState(null);
    const [notifSaving, setNotifSaving] = useState(false);
    // 載入完成前一律鎖住：初值 {} 會讓每一格都顯示成未勾選，此時若手快按下儲存，
    // 等於把機構全部的通知一次關光。這與 featureLoaded 是同一個理由。
    const [notifLoaded, setNotifLoaded] = useState(false);

    // [A3] 車號管理（家長）：最多 2 組，實際驗證/正規化在後端 service 層
    const [carPlates, setCarPlates] = useState(['', '']);
    const [carPlatesLoading, setCarPlatesLoading] = useState(false);

    // [BL-006 2026-08-19] 關鍵詞提醒（機構設定，僅 admin/super_admin）
    const [kwRules, setKwRules] = useState([]);
    const [kwInput, setKwInput] = useState('');
    const [kwError, setKwError] = useState('');      // 顯示在輸入框下方，取代靜默拒絕
    const [kwAdding, setKwAdding] = useState(false);
    const [kwLoaded, setKwLoaded] = useState(false);
    // 下限由後端回傳，不在前端寫死：兩邊各寫一份數字遲早會分岔，
    // 而分岔的症狀是「前端說可以、後端回 400」這種看起來像壞掉的畫面。
    const [kwMinLength, setKwMinLength] = useState(2);
    const [kwHits, setKwHits] = useState(null);      // null = 尚未載入（點開才抓）
    const [kwHitsLoading, setKwHitsLoading] = useState(false);

    useEffect(() => {
        if (user?.role === 'parent') {
            const existing = user?.car_plates || [];
            setCarPlates([existing[0] || '', existing[1] || '']);
        }
    }, [user]);

    // Fetch Institution logic
    useEffect(() => {
        if (user?.institution_id && (user?.role === 'admin' || user?.role === 'super_admin')) {
            authAPI.getInstitution(user.institution_id).then(res => {
                setInstSettings({
                    attendance_delay_minutes: res.data.attendance_delay_minutes || 5,
                    name: res.data.name,
                    daily_reset_time: res.data.daily_reset_time?.substring(0, 5) || '03:00', // HH:MM
                    prediction_start_time: res.data.prediction_start_time?.substring(0, 5) || '17:00',
                    prediction_end_time: res.data.prediction_end_time?.substring(0, 5) || '20:00',
                    closing_report_time: res.data.closing_report_time?.substring(0, 5) || '21:30',
                    line_notify_token: res.data.line_notify_token || ''
                });
                // [A2] 只有 super_admin 會顯示/使用這些欄位，但一律存起來無妨（不影響一般 admin 畫面）
                setInstFeatures({
                    display_name: res.data.display_name || '',
                    logo_url: res.data.logo_url || '',
                });
                const gl = res.data.grade_labels || {};
                setGradeLabelRows(Object.entries(gl).map(([grade, label]) => ({ grade, label })));
                // [2026-08-17] App 功能：兩個欄位都是後端解析好的完整 dict，不需要再併預設值
                setFeatureGranted({ ...(res.data.granted_features || {}) });
                setFeatureEnabled({ ...(res.data.features || {}) });
                setFeatureLoaded(true);
                // [2026-08-20] 通知矩陣：後端回的是解析好的完整 6 列，直接當草稿初值。
                // baseline 另存一份原值（不共用同一個物件參考，否則勾選時會連 baseline
                // 一起改掉，樂觀鎖就永遠比對成功而形同虛設）。
                const matrix = res.data.staff_permissions || {};
                setNotifMatrix(JSON.parse(JSON.stringify(matrix)));
                setNotifBaseline(JSON.parse(JSON.stringify(matrix)));
                setNotifLoaded(true);
            }).catch(err => console.error(err));
        }
    }, [user]);

    // 通知項目的清單與說明。與機構設定分開抓：這一份是**跟機構無關的靜態定義**，
    // 就算某間機構的設定抓失敗，也還是應該看得到有哪些項目可以設定。
    useEffect(() => {
        if (user?.role === 'admin' || user?.role === 'super_admin') {
            authAPI.getStaffPermissionOptions()
                .then(res => setNotifOptions(res.data))
                .catch(err => console.error(err));
        }
    }, [user?.role]);

    const handleUpdateInstitution = async () => {
        if (!user?.institution_id) return;
        try {
            setInstLoading(true);
            await authAPI.updateInstitution(user.institution_id, {
                attendance_delay_minutes: instSettings.attendance_delay_minutes,
                daily_reset_time: instSettings.daily_reset_time, // Send HH:MM
                prediction_start_time: instSettings.prediction_start_time,
                prediction_end_time: instSettings.prediction_end_time,
                closing_report_time: instSettings.closing_report_time,
                line_notify_token: instSettings.line_notify_token
            });
            toast({ title: "機構設定已更新", status: "success", isClosable: true });
        } catch (error) {
            toast({ title: "更新失敗", description: error.message, status: "error" });
        } finally {
            setInstLoading(false);
        }
    };

    // [2026-08-17] 儲存機構自己的功能啟用選擇（不碰 granted_features——那是超管的權限，
    // 一般 admin 只要帶上這個欄位後端就會回 403）。
    const handleUpdateAppFeatures = async () => {
        if (!user?.institution_id) return;
        try {
            setFeatureSaving(true);
            // 只送出「已獲授權」的項目。後端是整包覆寫 features，而未授權項目在這個畫面上
            // 的值恆為 false（生效值＝granted AND enabled），照送等於把它的啟用層釘死成關閉；
            // 日後超管授予時會變成「授權了卻還是沒開」。省略該 key＝沿用後端預設值。
            const payload = Object.keys(featureEnabled).reduce((acc, key) => {
                if (featureGranted[key] !== false) acc[key] = !!featureEnabled[key];
                return acc;
            }, {});
            await authAPI.updateInstitution(user.institution_id, { features: payload });
            toast({ title: "App 功能已更新", status: "success", isClosable: true });
        } catch (error) {
            // 後端的 detail 會指名哪幾項未獲授權，對使用者有意義，別用罐頭訊息蓋掉
            toast({
                title: "更新失敗",
                description: error.response?.data?.detail || error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setFeatureSaving(false);
        }
    };

    // [2026-08-20] 教職員通知設定
    const handleToggleNotif = (capability, role, checked) => {
        setNotifMatrix(prev => {
            const current = prev[capability] || [];
            // 去重：同一個職位若不小心進去兩次，樂觀鎖比對時會與後端解析出來的
            // （已去重的）值不一致，下一次儲存就會冒出一個沒有人改過的 409。
            const next = checked
                ? [...new Set([...current, role])]
                : current.filter(r => r !== role);
            return { ...prev, [capability]: next };
        });
    };

    const handleResetNotifToDefault = () => {
        if (!notifOptions) return;
        // 「還原預設」必須說明預設是什麼，不能是一顆按下去才知道結果的按鈕。
        const summary = notifOptions.capabilities
            .map(c => `・${c.label}：${c.default_roles
                .map(r => notifOptions.roles.find(x => x.name === r)?.label || r)
                .join('、') || '（不通知任何人）'}`)
            .join('\n');
        if (!window.confirm(`要把全部通知還原成系統預設嗎？

${summary}

還原後仍需按「儲存通知設定」才會生效。`)) return;
        const next = {};
        notifOptions.capabilities.forEach(c => { next[c.name] = [...c.default_roles]; });
        setNotifMatrix(next);
    };

    const handleUpdateStaffNotifications = async () => {
        if (!user?.institution_id || !notifOptions) return;
        try {
            setNotifSaving(true);
            // 送出**整張矩陣**（全部 6 列）而不是只送改動的那幾列：後端是整包覆寫，
            // 只送一列的話其餘五列會被當成「還原成預設」。
            const payload = {};
            notifOptions.capabilities.forEach(c => {
                payload[c.name] = notifMatrix[c.name] || [];
            });
            await authAPI.updateInstitution(user.institution_id, {
                staff_permissions: payload,
                // 樂觀鎖：帶上載入當下的值，被別人改過就讓後端擋下來（409）。
                staff_permissions_expected: notifBaseline || undefined,
            });
            // 存檔成功後 baseline 要跟上，否則同一個畫面連按兩次儲存，第二次會誤判成衝突。
            setNotifBaseline(JSON.parse(JSON.stringify(payload)));
            toast({ title: "通知設定已更新", status: "success", isClosable: true });
        } catch (error) {
            // 409（被別人改過）與 400（格式錯誤）的 detail 對使用者都有意義，
            // 別用罐頭訊息蓋掉——尤其 409 要讓他知道「重新整理後再存一次」。
            toast({
                title: "更新失敗",
                description: error.response?.data?.detail || error.message,
                status: "error",
                duration: 8000,
                isClosable: true,
            });
        } finally {
            setNotifSaving(false);
        }
    };

    // [A2] super_admin 限定：品牌（display_name/logo_url）+ 年級標籤。
    // 獨立成自己的 handler/API 呼叫，不動 handleUpdateInstitution，確保一般 admin 的既有
    // 「儲存全部設定」行為逐字不變。
    const addGradeLabelRow = () => setGradeLabelRows(prev => [...prev, { grade: '', label: '' }]);
    const removeGradeLabelRow = (idx) => setGradeLabelRows(prev => prev.filter((_, i) => i !== idx));
    const updateGradeLabelRow = (idx, field, value) => {
        setGradeLabelRows(prev => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
    };

    const handleUpdateInstFeatures = async () => {
        if (!user?.institution_id) return;
        const gradeLabels = gradeLabelRows.reduce((acc, row) => {
            const g = (row.grade || '').toString().trim();
            if (g) acc[g] = row.label || '';
            return acc;
        }, {});
        try {
            setInstFeaturesLoading(true);
            // features 不在這裡送：功能啟用已改由上方「App 功能」區塊統一管理（含未授權標示），
            // 兩處各自持有草稿會互相蓋掉對方的結果。
            await authAPI.updateInstitution(user.institution_id, {
                display_name: instFeatures.display_name || null,
                logo_url: instFeatures.logo_url || null,
                grade_labels: gradeLabels,
            });
            toast({ title: "品牌與年級標籤已更新", status: "success", isClosable: true });
        } catch (error) {
            toast({
                title: "更新失敗",
                description: error.response?.data?.detail || error.message,
                status: "error",
                isClosable: true,
            });
        } finally {
            setInstFeaturesLoading(false);
        }
    };

    // ── [BL-006] 關鍵詞提醒 ─────────────────────────────────────────────
    // 家長訊息若含這些詞 → 系統通知機構的行政/管理員去看那則對話。
    // 這是「管理員平常不收家長訊息推播」（2026-08-19 起只有帶班老師收）那條規則的例外通道，
    // 所以清單的正確性直接決定管理員會不會漏掉真正該處理的事。
    const loadKeywordRules = async () => {
        try {
            const res = await authAPI.chat.keywordRules();
            setKwRules(res.data?.rules || []);
            if (res.data?.min_length) setKwMinLength(res.data.min_length);
        } catch (error) {
            console.error(error);
        } finally {
            setKwLoaded(true);
        }
    };

    useEffect(() => {
        if (user?.role === 'admin' || user?.role === 'super_admin') {
            loadKeywordRules();
        }
    }, [user?.role]);

    // 與後端 keyword_filter_service.normalize 同一套（NFKC ＋ 去空白）。
    // 這裡只拿來算長度，所以不需要 casefold（大小寫不影響字數）。
    // 用展開運算子計字數而非 .length：JS 的 .length 數的是 UTF-16 code unit，
    // 一個表情符號會被算成 2，跟後端用 code point 計算的結果不一致。
    const normalizedLength = (raw) => [...(raw || '').normalize('NFKC').replace(/\s+/g, '')].length;

    const handleAddKeyword = async () => {
        const raw = kwInput.trim();
        // 【刻意不用 isDisabled 把按鈕鎖起來】被鎖住的按鈕不會告訴管理員為什麼，
        // 他最可能的反應是換個字再試、或以為畫面壞了。這條兩字下限是設計取捨
        // （中文沒有詞界，單字子字串比對會大量誤中），值得在他按下去的當下解釋清楚。
        if (!raw) {
            setKwError('請輸入要加入的詞語。');
            return;
        }
        if (normalizedLength(raw) < kwMinLength) {
            setKwError(
                `詞語至少要 ${kwMinLength} 個字。系統用「包含即命中」的方式比對，` +
                '而中文沒有詞界——只設一個「不」字會連「不會」「不用」「對不起」都一起命中，' +
                '通知會多到失去意義。請改用完整的詞，例如「退費」「投訴」「換老師」。'
            );
            return;
        }
        setKwError('');
        try {
            setKwAdding(true);
            await authAPI.chat.addKeywordRule(raw);
            setKwInput('');
            await loadKeywordRules();
            toast({ title: '已加入關鍵詞', status: 'success', isClosable: true });
        } catch (error) {
            // 後端的 detail 寫的是「為什麼」（重複、超量、少於兩字），比罐頭訊息有用得多。
            // 放進 kwError 而不是 toast：錯誤要留在輸入框旁邊，管理員才看得到自己打了什麼。
            setKwError(error.response?.data?.detail || error.message || '新增失敗');
        } finally {
            setKwAdding(false);
        }
    };

    const handleToggleKeyword = async (rule) => {
        try {
            await authAPI.chat.setKeywordRuleActive(rule.id, !rule.is_active);
            await loadKeywordRules();
        } catch (error) {
            toast({
                title: '更新失敗',
                description: error.response?.data?.detail || error.message,
                status: 'error', isClosable: true,
            });
        }
    };

    const handleDeleteKeyword = async (rule) => {
        // 刪除會失去「誰在何時加的」，而停用不會——先讓管理員知道有這個選項再決定。
        if (!window.confirm(
            `確定要刪除「${rule.keyword}」嗎？\n\n` +
            '如果只是暫時不想收到這個詞的提醒，可以改用左側開關「停用」，' +
            '這樣會保留是誰在何時加入的紀錄。\n' +
            '（已經發生過的命中紀錄不會因為刪除而消失）'
        )) return;
        try {
            await authAPI.chat.deleteKeywordRule(rule.id);
            await loadKeywordRules();
        } catch (error) {
            toast({
                title: '刪除失敗',
                description: error.response?.data?.detail || error.message,
                status: 'error', isClosable: true,
            });
        }
    };

    // 命中紀錄點開才抓：多數時候管理員只是來改詞表，不必為此多打一支查詢。
    const handleToggleHits = async () => {
        if (kwHits !== null) {
            setKwHits(null);
            return;
        }
        try {
            setKwHitsLoading(true);
            const res = await authAPI.chat.keywordHits({ days: 30, limit: 50 });
            setKwHits(res.data || { total: 0, hits: [] });
        } catch (error) {
            toast({
                title: '讀取命中紀錄失敗',
                description: error.response?.data?.detail || error.message,
                status: 'error', isClosable: true,
            });
        } finally {
            setKwHitsLoading(false);
        }
    };

    // [A3] 家長登錄常用車號（最多 2 組）。後端 service 層做 normalize（去空白/轉大寫）、
    // 去重、單組長度上限，超過限制回 400（在這裡顯示為 toast 錯誤訊息）。
    const handleUpdateCarPlates = async () => {
        try {
            setCarPlatesLoading(true);
            const payload = carPlates.map((p) => (p || '').trim()).filter(Boolean);
            const res = await authAPI.updateCarPlates(payload);
            const saved = res.data?.car_plates || [];
            setCarPlates([saved[0] || '', saved[1] || '']);
            toast({ title: "車號已更新", status: "success", isClosable: true });
        } catch (error) {
            toast({
                title: "車號更新失敗",
                description: error.response?.data?.detail || error.message,
                status: "error",
                isClosable: true,
            });
        } finally {
            setCarPlatesLoading(false);
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswords(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitPassword = async () => {
        if (!passwords.old_password || !passwords.new_password || !passwords.confirm_password) {
            toast({ title: "請填寫所有欄位", status: "warning", isClosable: true });
            return;
        }
        if (passwords.new_password !== passwords.confirm_password) {
            toast({ title: "新密碼與確認密碼不符", status: "error", isClosable: true });
            return;
        }
        if (passwords.new_password.length < 8) {
            toast({ title: "新密碼長度需至少 8 碼", status: "warning", isClosable: true });
            return;
        }

        try {
            setLoading(true);
            await authAPI.updatePassword({
                old_password: passwords.old_password,
                new_password: passwords.new_password
            });
            toast({ title: "密碼修改成功", status: "success", isClosable: true });
            toast({ title: "密碼修改成功", status: "success", isClosable: true });
            onPassClose();
            setPasswords({ old_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            console.error(error);
            toast({
                title: "密碼修改失敗",
                description: error.response?.data?.detail || "舊密碼可能錯誤",
                status: "error",
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };



    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleDeleteAccount = async () => {
        try {
            setLoading(true);
            await authAPI.deleteMe();
            toast({
                title: "帳號已刪除",
                description: "您的帳號已成功停用，感謝您的使用。",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            onDeleteClose();
            // Wait a bit for toast then logout
            setTimeout(() => {
                logout();
                navigate('/login');
            }, 1000);
        } catch (error) {
            console.error("Delete account error:", error);
            toast({
                title: "刪除失敗",
                description: "請稍後再試，或聯繫管理員。",
                status: "error",
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box h="100%" overflowY="auto" px={4} py={6}>
            <Box maxW="600px" mx="auto">
                <Heading mb={6} size="xl" color="brand.800">系統設定</Heading>

                <VStack spacing={6} align="stretch" bg="white" p={6} borderRadius="2xl" shadow="sm" border="1px solid" borderColor="gray.100">
                    {/* User Info Section */}
                    <Box>
                        <Heading size="md" mb={3} display="flex" alignItems="center">
                            <Icon as={FiUser} mr={3} color="brand.500" boxSize={6} />
                            個人資訊
                        </Heading>
                        <Text color="gray.600" fontSize="lg" mb={1}>使用者: {user?.full_name}</Text>
                        <Text color="gray.600" fontSize="lg">角色: {user?.role}</Text>
                    </Box>

                    <Divider />

                    {/* Account Security */}
                    <Box>
                        <Heading size="md" mb={4} display="flex" alignItems="center">
                            <Icon as={FiLock} mr={3} color="orange.500" boxSize={6} />
                            帳號安全
                        </Heading>
                        <Button
                            leftIcon={<Icon as={FiLock} boxSize={5} />}
                            onClick={onPassOpen}
                            variant="ghost"
                            justifyContent="flex-start"
                            colorScheme="orange"
                            w="full"
                            size="lg"
                            h="60px"
                            fontSize="xl"
                        >
                            修改密碼
                        </Button>
                    </Box>

                    <Divider />

                    {/* [A3] 車號管理（家長專屬；僅名下任一孩子所屬機構開啟 features.car_plate 才顯示） */}
                    {user?.role === 'parent' && getUnionFeatures(user?.children).car_plate && (
                        <>
                            <Box>
                                <Heading size="md" mb={2} display="flex" alignItems="center">
                                    <Icon as={IoCarSportOutline} mr={3} color="teal.500" boxSize={6} />
                                    車號管理
                                </Heading>
                                <Text color="var(--fg-muted)" fontSize="sm" mb={4}>
                                    登錄常用車號，出發接送時可快速選用（最多 2 組）。
                                </Text>

                                <FormControl mb={4}>
                                    <FormLabel fontSize="lg">車號 1</FormLabel>
                                    <Input
                                        maxW="300px"
                                        placeholder="例如：ABC-1234"
                                        value={carPlates[0]}
                                        onChange={(e) => setCarPlates((prev) => [e.target.value, prev[1]])}
                                        size="lg"
                                    />
                                </FormControl>

                                <FormControl mb={5}>
                                    <FormLabel fontSize="lg">車號 2</FormLabel>
                                    <Input
                                        maxW="300px"
                                        placeholder="例如：XYZ-5678"
                                        value={carPlates[1]}
                                        onChange={(e) => setCarPlates((prev) => [prev[0], e.target.value])}
                                        size="lg"
                                    />
                                </FormControl>

                                <Button
                                    onClick={handleUpdateCarPlates}
                                    isLoading={carPlatesLoading}
                                    colorScheme="teal"
                                    mt={2}
                                    size="lg"
                                    w="full"
                                    h="50px"
                                    fontSize="lg"
                                >
                                    儲存車號
                                </Button>
                            </Box>
                            <Divider />
                        </>
                    )}

                    {/* Institution Settings (Admin Only) */}
                    {(user?.role === 'admin' || user?.role === 'super_admin') && (
                        <>
                            <Box>
                                <Heading size="md" mb={4} display="flex" alignItems="center">
                                    <Icon as={FiHome} mr={3} color="purple.500" boxSize={6} />
                                    機構設定 ({instSettings.name})
                                </Heading>

                                {/* Attendance Delay */}
                                <FormControl mb={5}>
                                    <FormLabel fontSize="lg">自動點名延遲時間 (分鐘)</FormLabel>
                                    <Box display="flex" gap={2} maxW="300px">
                                        <NumberInput
                                            min={1} max={60}
                                            value={instSettings.attendance_delay_minutes}
                                            onChange={(val) => setInstSettings(prev => ({ ...prev, attendance_delay_minutes: parseInt(val) }))}
                                            size="lg"
                                        >
                                            <NumberInputField fontSize="lg" />
                                            <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                            </NumberInputStepper>
                                        </NumberInput>
                                    </Box>
                                </FormControl>

                                <Divider my={4} />

                                {/* Daily Reset Time */}
                                <FormControl mb={5}>
                                    <FormLabel fontSize="lg">每日自動重置時間</FormLabel>
                                    <Input
                                        type="time"
                                        maxW="200px"
                                        value={instSettings.daily_reset_time || "03:00"}
                                        onChange={(e) => setInstSettings(prev => ({ ...prev, daily_reset_time: e.target.value }))}
                                        size="lg"
                                    />
                                </FormControl>

                                <Divider my={4} />

                                {/* Prediction Window */}
                                <FormControl mb={5}>
                                    <FormLabel fontSize="lg">智慧預測運作時段</FormLabel>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Input
                                            type="time"
                                            maxW="180px"
                                            value={instSettings.prediction_start_time || "17:00"}
                                            onChange={(e) => setInstSettings(prev => ({ ...prev, prediction_start_time: e.target.value }))}
                                            size="lg"
                                        />
                                        <Text fontSize="lg">至</Text>
                                        <Input
                                            type="time"
                                            maxW="180px"
                                            value={instSettings.prediction_end_time || "20:00"}
                                            onChange={(e) => setInstSettings(prev => ({ ...prev, prediction_end_time: e.target.value }))}
                                            size="lg"
                                        />
                                    </Box>
                                </FormControl>

                                <Button onClick={handleUpdateInstitution} isLoading={instLoading} colorScheme="purple" mt={2} size="lg" w="full" h="50px" fontSize="lg">
                                    儲存全部設定
                                </Button>
                            </Box>
                            <Divider />

                            {/* [2026-08-17] App 功能 —— 兩層授權模型的第二層（機構管理員自己的啟用選擇）。
                                刻意包在 admin/super_admin 條件內：這個頁面同時掛在 /settings（桌面管理端）
                                與 /parent/settings（家長 App），家長、老師、櫃檯都不該看到機構層級設定。 */}
                            <Box>
                                <Heading size="md" mb={2} display="flex" alignItems="center">
                                    <Icon as={FiSmartphone} mr={3} color="brand.500" boxSize={6} />
                                    App 功能
                                </Heading>
                                {/* 只寫「家長 App」而不寫「老師端」：目前前端的 features 判斷全都在家長端
                                    （MobileBottomNav / ParentDashboard / ParentPickupPage…），老師端沒有做入口
                                    隱藏，寫上去會變成做不到的承諾。生效時機取決於家長端 getMe() 重新取回機構
                                    資料，也就是下次開啟 App 時。 */}
                                <Text color="var(--fg-muted)" fontSize="sm" mb={2}>
                                    關閉的功能，家長 App 就不會出現對應入口（家長下次開啟 App 時生效）。
                                    標示「未授權」的項目請聯繫超級管理員開通後才能啟用。
                                </Text>

                                <InstitutionFeatureSwitches
                                    mode="enable"
                                    granted={featureGranted}
                                    enabled={featureEnabled}
                                    onChange={(key, val) => setFeatureEnabled(prev => ({ ...prev, [key]: val }))}
                                    isDisabled={featureSaving || !featureLoaded}
                                />

                                <Button
                                    onClick={handleUpdateAppFeatures}
                                    isLoading={featureSaving}
                                    isDisabled={!featureLoaded}
                                    colorScheme="brand"
                                    mt={5}
                                    size="lg"
                                    w="full"
                                    h="50px"
                                    fontSize="lg"
                                >
                                    儲存 App 功能
                                </Button>
                            </Box>
                            <Divider />

                            {/* [BL-006 2026-08-19] 關鍵詞提醒 —— 機構管理員專用（同樣包在
                                admin/super_admin 條件內：這個頁面也掛在 /parent/settings）。
                                每項操作即時生效，沒有「儲存」按鈕：詞表是一列一列的資料而不是
                                一份草稿，做成草稿反而會讓人以為改完就生效卻忘了按儲存。 */}
                            <Box>
                                <Heading size="md" mb={2} display="flex" alignItems="center">
                                    <Icon as={FiAlertTriangle} mr={3} color="orange.500" boxSize={6} />
                                    關鍵詞提醒
                                </Heading>
                                <Text color="var(--fg-muted)" fontSize="sm" mb={1}>
                                    家長傳送的訊息若包含以下詞語，系統會通知機構的行政與管理員去看那則對話。
                                </Text>
                                <Text color="var(--fg-muted)" fontSize="sm" mb={1}>
                                    平常家長訊息的通知只會發給帶班老師，這裡設定的詞是唯一的例外通道；
                                    同一條對話短時間內連續命中只會提醒一次，避免通知被灌爆。
                                </Text>
                                {/* 隱私：這是對家長訊息內容做自動掃描。機構本來就看得到訊息，
                                    但「自動掃描並告警」是不同層級的事，管理員在開啟這個功能之前
                                    應該先知道自己啟用的是什麼。 */}
                                <Text color="var(--fg-muted)" fontSize="sm" mb={4}>
                                    ⚠️ 啟用後，系統會自動比對每一則家長訊息的文字內容。
                                    機構自己發出的群發與聯絡簿不會被比對。
                                </Text>

                                <FormControl isInvalid={!!kwError} mb={4}>
                                    <HStack align="flex-start">
                                        <Input
                                            placeholder={`輸入詞語（至少 ${kwMinLength} 個字），例如：退費`}
                                            value={kwInput}
                                            onChange={(e) => { setKwInput(e.target.value); setKwError(''); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddKeyword(); }}
                                            maxW="360px"
                                            size="lg"
                                        />
                                        <Button
                                            leftIcon={<FiPlus />}
                                            onClick={handleAddKeyword}
                                            isLoading={kwAdding}
                                            colorScheme="orange"
                                            size="lg"
                                        >
                                            新增
                                        </Button>
                                    </HStack>
                                    {/* 說明為什麼被擋下，而不只是把按鈕變灰 */}
                                    <FormErrorMessage>{kwError}</FormErrorMessage>
                                </FormControl>

                                {!kwLoaded ? (
                                    <Text color="var(--fg-muted)" fontSize="sm">載入中…</Text>
                                ) : kwRules.length === 0 ? (
                                    <Text color="var(--fg-muted)" fontSize="sm">
                                        目前沒有設定任何詞語，系統不會比對任何訊息。
                                    </Text>
                                ) : (
                                    <VStack align="stretch" spacing={2}>
                                        {kwRules.map((rule) => (
                                            <HStack
                                                key={rule.id}
                                                p={3}
                                                borderWidth="1px"
                                                borderRadius="md"
                                                spacing={3}
                                            >
                                                <Switch
                                                    isChecked={rule.is_active}
                                                    onChange={() => handleToggleKeyword(rule)}
                                                    colorScheme="orange"
                                                />
                                                <Text
                                                    fontWeight="bold"
                                                    fontSize="lg"
                                                    // 停用中的詞留在清單上但要一眼看得出沒在作用，
                                                    // 否則管理員會以為它還在保護自己
                                                    color={rule.is_active ? undefined : 'var(--fg-muted)'}
                                                >
                                                    {rule.keyword}
                                                </Text>
                                                {!rule.is_active && (
                                                    <Badge colorScheme="gray">已停用</Badge>
                                                )}
                                                <Box flex="1" />
                                                {/* 誰在何時加的：這份清單決定哪些家長訊息會被自動
                                                    告警，機構內部要能追溯是誰做的決定 */}
                                                {(rule.created_by_name || rule.created_at) && (
                                                    <Text fontSize="xs" color="var(--fg-muted)" textAlign="right">
                                                        {rule.created_by_name || '—'}
                                                        {rule.created_at
                                                            ? ` · ${new Date(rule.created_at).toLocaleDateString('zh-TW')}`
                                                            : ''}
                                                    </Text>
                                                )}
                                                <IconButton
                                                    icon={<FiTrash2 />}
                                                    aria-label={`刪除 ${rule.keyword}`}
                                                    variant="ghost"
                                                    colorScheme="red"
                                                    onClick={() => handleDeleteKeyword(rule)}
                                                />
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}

                                {/* 命中紀錄：機構要能回答「上個月被攔到幾次、都是什麼詞」，
                                    這也是判斷某個詞該不該留下來的唯一依據。
                                    刻意不顯示訊息內容（後端也不存），要看內容請開該對話。 */}
                                <Button
                                    variant="link"
                                    colorScheme="orange"
                                    size="sm"
                                    mt={4}
                                    onClick={handleToggleHits}
                                    isLoading={kwHitsLoading}
                                >
                                    {kwHits !== null ? '收合命中紀錄' : '查看最近 30 天的命中紀錄'}
                                </Button>

                                {kwHits !== null && (
                                    <Box mt={3}>
                                        <Text fontSize="sm" color="var(--fg-muted)" mb={2}>
                                            最近 30 天共命中 {kwHits.total ?? 0} 次
                                            {(kwHits.hits?.length ?? 0) < (kwHits.total ?? 0)
                                                ? `（以下顯示最近 ${kwHits.hits.length} 筆）`
                                                : ''}
                                            。此處不顯示訊息內容，請至該對話查看。
                                        </Text>
                                        {(kwHits.hits?.length ?? 0) === 0 ? (
                                            <Text fontSize="sm" color="var(--fg-muted)">沒有命中紀錄。</Text>
                                        ) : (
                                            <VStack align="stretch" spacing={1}>
                                                {kwHits.hits.map((hit) => (
                                                    <HStack key={hit.id} fontSize="sm" spacing={3}>
                                                        <Badge colorScheme="orange">{hit.keyword}</Badge>
                                                        <Text>{hit.student_name || '—'}</Text>
                                                        <Box flex="1" />
                                                        {/* 被冷卻壓下的命中要標示出來，否則管理員會
                                                            以為每一次命中他都收到過通知 */}
                                                        {!hit.notified && (
                                                            <Text fontSize="xs" color="var(--fg-muted)">
                                                                （同串連續命中，未再提醒）
                                                            </Text>
                                                        )}
                                                        <Text fontSize="xs" color="var(--fg-muted)">
                                                            {hit.created_at
                                                                ? new Date(hit.created_at).toLocaleString('zh-TW')
                                                                : ''}
                                                        </Text>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        )}
                                    </Box>
                                )}
                            </Box>
                            <Divider />

                            {/* [2026-08-20 BL-005] 教職員通知設定 —— 機構管理員專用。
                                放在「關鍵詞提醒」之後：上一區設定「哪些詞會觸發告警」，
                                這一區設定「告警與其他通知要發給誰」，是同一件事的兩半。

                                【這一區存在的理由】收件人在 2026-08-12 到 08-20 的 8 天內被改了
                                4 次，每一次都是改程式碼再等 Railway 重新部署。做錯的後果只是
                                手機多響或少響，看得見也改得回來——所以它適合做成設定；
                                而「誰看得到哪些學生／對話」那類權限刻意**沒有**放進來，
                                因為做錯是無聲且不可回復的（見後端 core/staff_permissions.py 檔頭）。 */}
                            <Box>
                                <Heading size="md" mb={2} display="flex" alignItems="center">
                                    <Icon as={FiBell} mr={3} color="brand.500" boxSize={6} />
                                    教職員通知設定
                                </Heading>
                                <Text color="var(--fg-muted)" fontSize="sm" mb={1}>
                                    勾選哪些職位要收到哪一種通知。只影響「誰的手機會響」，
                                    不影響任何人看得到什麼——沒收到通知的人一樣進得去看。
                                </Text>
                                <Text color="var(--fg-muted)" fontSize="sm" mb={4}>
                                    未調整過的項目沿用系統預設；調整過的項目會標示「已調整」。
                                </Text>

                                {!notifOptions || !notifLoaded ? (
                                    <Text color="var(--fg-muted)" fontSize="sm">載入中…</Text>
                                ) : (
                                    <>
                                        <StaffNotificationMatrix
                                            roles={notifOptions.roles}
                                            capabilities={notifOptions.capabilities}
                                            value={notifMatrix}
                                            onToggle={handleToggleNotif}
                                            isDisabled={notifSaving}
                                        />

                                        <Button
                                            onClick={handleUpdateStaffNotifications}
                                            isLoading={notifSaving}
                                            colorScheme="brand"
                                            mt={5}
                                            size="lg"
                                            w="full"
                                            h="50px"
                                            fontSize="lg"
                                        >
                                            儲存通知設定
                                        </Button>

                                        {/* 還原預設放在儲存之後、做成 link 而不是按鈕：
                                            它是少用的逃生口，不該跟主要動作長得一樣重。 */}
                                        <Button
                                            variant="link"
                                            colorScheme="brand"
                                            size="sm"
                                            mt={4}
                                            onClick={handleResetNotifToDefault}
                                            isDisabled={notifSaving}
                                        >
                                            還原成系統預設
                                        </Button>
                                    </>
                                )}
                            </Box>
                            <Divider />

                            {/* [A2] 機構品牌 / 年級標籤 —— 僅 super_admin 顯示與可改。
                                一般 admin 完全看不到這個區塊，畫面與 A2 之前逐字相同。 */}
                            {user?.role === 'super_admin' && (
                                <>
                                    <Box>
                                        <Heading size="md" mb={4} display="flex" alignItems="center">
                                            <Icon as={FiTag} mr={3} color="pink.500" boxSize={6} />
                                            機構品牌與年級標籤（超級管理員）
                                        </Heading>

                                        {/* Display Name */}
                                        <FormControl mb={5}>
                                            <FormLabel fontSize="lg">顯示名稱（家長端顯示，留空則顯示機構原名稱）</FormLabel>
                                            <Input
                                                maxW="400px"
                                                placeholder={instSettings.name}
                                                value={instFeatures.display_name}
                                                onChange={(e) => setInstFeatures(prev => ({ ...prev, display_name: e.target.value }))}
                                                size="lg"
                                            />
                                        </FormControl>

                                        {/* Logo URL */}
                                        <FormControl mb={5}>
                                            <FormLabel fontSize="lg">Logo 網址</FormLabel>
                                            <Input
                                                maxW="400px"
                                                placeholder="https://..."
                                                value={instFeatures.logo_url}
                                                onChange={(e) => setInstFeatures(prev => ({ ...prev, logo_url: e.target.value }))}
                                                size="lg"
                                            />
                                        </FormControl>

                                        <Divider my={4} />

                                        {/* Grade Labels */}
                                        <Text fontSize="lg" fontWeight="bold" mb={1}>年級標籤對照</Text>
                                        <Text fontSize="sm" color="var(--fg-muted)" mb={3}>
                                            留空＝沿用預設「N 年級」顯示。幼稚園可設定例如 1=幼幼班、2=小班、3=中班、4=大班。
                                        </Text>
                                        <VStack align="stretch" spacing={2} mb={3}>
                                            {gradeLabelRows.map((row, idx) => (
                                                <HStack key={idx}>
                                                    <Input
                                                        placeholder="年級數字"
                                                        maxW="110px"
                                                        value={row.grade}
                                                        onChange={(e) => updateGradeLabelRow(idx, 'grade', e.target.value)}
                                                    />
                                                    <Input
                                                        placeholder="顯示名稱（例如：幼幼班）"
                                                        value={row.label}
                                                        onChange={(e) => updateGradeLabelRow(idx, 'label', e.target.value)}
                                                    />
                                                    <IconButton
                                                        icon={<FiTrash2 />}
                                                        aria-label="刪除此列"
                                                        variant="ghost"
                                                        colorScheme="red"
                                                        onClick={() => removeGradeLabelRow(idx)}
                                                    />
                                                </HStack>
                                            ))}
                                        </VStack>
                                        <Button leftIcon={<FiPlus />} size="sm" variant="outline" onClick={addGradeLabelRow} mb={5}>
                                            新增年級標籤
                                        </Button>

                                        <Button
                                            onClick={handleUpdateInstFeatures}
                                            isLoading={instFeaturesLoading}
                                            colorScheme="pink"
                                            mt={2}
                                            size="lg"
                                            w="full"
                                            h="50px"
                                            fontSize="lg"
                                        >
                                            儲存品牌與年級標籤
                                        </Button>
                                    </Box>
                                    <Divider />
                                </>
                            )}
                        </>
                    )}

                    {/* Logout Action */}
                    <Button
                        leftIcon={<FiLogOut />}
                        colorScheme="red"
                        variant="ghost"
                        size="lg"
                        w="full"
                        h="60px"
                        fontSize="xl"
                        onClick={handleLogout}
                    >
                        登出系統
                    </Button>

                    <Divider />

                    {/* Danger Zone */}
                    <Box pt={2}>
                        <Heading size="md" mb={2} color="red.500" display="flex" alignItems="center">
                            ⚠️ 危險區域
                        </Heading>
                        <Text color="var(--fg-muted)" mb={4} fontSize="sm">
                            刪除帳號後，您將無法再登入使用本系統，且無法接收任何推播通知。
                            若要恢復帳號，請聯繫機構管理員。
                        </Text>
                        <Button
                            colorScheme="red"
                            variant="outline"
                            size="md"
                            w="full"
                            onClick={onDeleteOpen}
                            borderColor="red.200"
                            _hover={{ bg: 'red.50' }}
                        >
                            刪除我的帳號
                        </Button>
                    </Box>
                </VStack>

                {/* Change Password Modal */}
                <Modal isOpen={isPassOpen} onClose={onPassClose} isCentered size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader fontSize="xl">修改密碼</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="lg">目前密碼</FormLabel>
                                <Input
                                    type="password"
                                    name="old_password"
                                    value={passwords.old_password}
                                    onChange={handlePasswordChange}
                                    placeholder="請輸入目前的密碼"
                                    size="lg"
                                />
                            </FormControl>

                            <FormControl mb={5} isRequired>
                                <FormLabel fontSize="lg">新密碼 (至少8碼)</FormLabel>
                                <Input
                                    type="password"
                                    name="new_password"
                                    value={passwords.new_password}
                                    onChange={handlePasswordChange}
                                    placeholder="請輸入新密碼"
                                    size="lg"
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="lg">確認新密碼</FormLabel>
                                <Input
                                    type="password"
                                    name="confirm_password"
                                    value={passwords.confirm_password}
                                    onChange={handlePasswordChange}
                                    placeholder="再次輸入新密碼"
                                    size="lg"
                                />
                            </FormControl>
                        </ModalBody>

                        <ModalFooter>
                            <Button onClick={onPassClose} mr={3} size="lg">取消</Button>
                            <Button colorScheme="orange" onClick={handleSubmitPassword} isLoading={loading} size="lg">
                                確認修改
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </Box>

            {/* Delete Account Confirmation Modal */}
            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered size="sm">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color="red.500">確認刪除帳號？</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text mb={4}>
                            此動作將無法復原。您的帳號將被標記為停用，且您將無法再收到任何關於孩子的接送通知。
                        </Text>
                        <Text fontWeight="bold">您確定要繼續嗎？</Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onDeleteClose} mr={3}>取消</Button>
                        <Button colorScheme="red" onClick={handleDeleteAccount} isLoading={loading}>
                            確認刪除
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default SettingsPage;
